/**
 * DECISIONS 207 (8) (the owner: "it'd be nice to have a map indicator for the dropship wave vehicle like we do for other
 * vehicles"): the side's dropships as the table and the corner map mark them — each in the air or down at its landing, where
 * it is over the ground (its pose, core/dropship.ts dropshipPose, at the state's tick), whether it is a wave's sortie (200
 * step 4) or a called ship, how many ride in it, and where it is bound while it comes in. The marks are drawn with the
 * `dropship` glyph (commandIcons.ts) in its team's colour. Presentation only: reads state, writes none.
 */
import { boardingSortie, parkedShips } from '../core/dropship';
import { flightNow } from '../core/shipNow';
import { DROPSHIP_PAYLOADS } from '../constants';
import type { DropshipFlight, GameState, Vec3 } from '../types';

/**
 * DECISIONS 207 (7) (the owner: *"make sure that it accurately depicts the number of troops actually in the vehicle"*): the
 * riders still aboard a flight and where each sits — the one reading the map's count and the ride-in camera's cabin share,
 * so that when the riders become real bodies aboard (207 (9), anti-air) this swaps its source, not their drawing. Today the
 * source is the flight's own: its `riders` in the order they leave, those from `out` on still aboard (the sim moves `out` as
 * each rider's exit tick passes). Each keeps one seat for the ride: the first out nearest the ramp, the walls in turn (the
 * container's seats, rendering/dropshipLooks.ts: the left wall's six from the ramp forward, then the right's). A rider leads
 * its squad when none of the squad ahead of it is alive (core/squadCards.ts squadLeaders' rule, for the dead aboard).
 */
export interface Rider { unitId: number; seat: number; squadId: number | null; leader: boolean }
/** The container's seats a wall (dropshipLooks DROPSHIP.box.seatZ). */
export const RIDE_ROWS = 6;
/** The seat of the rider `i`th to leave: the walls in turn from the ramp forward. */
export const rideSeat = (i: number): number => (i % 2) * RIDE_ROWS + Math.floor(i / 2);

const riderAt = (state: Pick<GameState, 'players' | 'squads'>, id: number, seat: number): Rider => {
  const sq = state.squads?.find(q => q.unitIds.includes(id)), ahead = sq ? sq.unitIds.slice(0, sq.unitIds.indexOf(id)) : [];
  return { unitId: id, seat, squadId: sq ? sq.id : null, leader: !!sq && !ahead.some(u => state.players.some(p => p.id === u && p.alive)) };
};

export function ridersAboard(state: Pick<GameState, 'players' | 'squads'>, f: DropshipFlight): Rider[] {
  if (f.payload !== 'troops') return [];
  const out: Rider[] = [];
  for (let i = f.out; i < f.riders.length; i++) out.push(riderAt(state, f.riders[i], rideSeat(i)));
  return out;
}

/**
 * DECISIONS 208 (the owner: *"leaning yes so you could wait the n seconds in the can before it takes off"*): the riders in the
 * side's wave ship parked on its pad — its boarding sortie's units back alive and boarded (core/dropship.ts boardShips), in
 * the order they boarded, which is their seat (padSeat's) and, at take-off, their place among the flight's riders (so each
 * keeps its seat from the pad to the ramp). At most a container's seats.
 */
export function ridersParked(state: GameState, team: number): Rider[] {
  const q = boardingSortie(state, team);
  if (!q) return [];
  const ids = q.units.filter(id => state.players.some(p => p.id === id && p.alive && p.boarded)).slice(0, DROPSHIP_PAYLOADS.troops.seats);
  return ids.map((id, k) => riderAt(state, id, rideSeat(k)));
}

/**
 * The ship unit `unitId` rides at the state's tick — a flight in the air (207 (7)) or (208) its side's wave ship on its pad,
 * boarded — its key (the flight's id, or −1 − team parked: the host's view of that ship), its riders aboard and its own seat;
 * null when it rides none.
 */
export function rideOf(state: GameState, unitId: number): { key: number; flight: DropshipFlight | null; aboard: Rider[]; rider: Rider } | null {
  for (const f of state.dropships?.flights ?? []) {
    const i = f.riders.indexOf(unitId);
    if (i < f.out || !flightNow(state, f, state.tick ?? 0)) continue;   // (69.17 step 1: the ship as it is now)
    const aboard = ridersAboard(state, f), rider = aboard.find(r => r.unitId === unitId);
    if (rider) return { key: f.id, flight: f, aboard, rider };
  }
  const p = state.players.find(x => x.id === unitId);
  if (p?.alive && p.boarded && parkedShips(state).some(s => s.team === p.teamIndex)) {
    const aboard = ridersParked(state, p.teamIndex), rider = aboard.find(r => r.unitId === unitId);
    if (rider) return { key: -1 - p.teamIndex, flight: null, aboard, rider };
  }
  return null;
}

export interface DropshipMark {
  id: number;
  team: number;
  planetIndex: number;
  /** The surface direction under it. */
  dir: Vec3;
  /** In the air (in or out), down at its landing (the ramp let down, or a vehicle lowered), or (207 (1)) the wave ship idling on its pad between runs. */
  state: 'flying' | 'landed' | 'parked';
  /** A wave's sortie (GameConfig.waveShips), not a called ship. */
  wave: boolean;
  /** Troops still aboard (none for a vehicle drop); parked, those boarded for its next run (208: ridersParked). */
  riders: number;
  /** Where it will land, while it comes in. */
  to?: Vec3;
}

/** The side's dropships at the state's tick (none before a call or once gone), and its wave ship parked on its pad (core/dropship.ts parkedShips; its id −1 − team). */
export function dropshipMarks(state: Pick<GameState, 'dropships' | 'planets' | 'config' | 'tick' | 'players' | 'squads' | 'shipPads' | 'sorties'>, team: number): DropshipMark[] {
  const tick = state.tick ?? 0, out: DropshipMark[] = [];
  for (const p of parkedShips(state)) {
    if (p.team !== team) continue;
    out.push({ id: -1 - team, team, planetIndex: p.planetIndex, dir: p.dir, state: 'parked', wave: true, riders: ridersParked(state as GameState, team).length });   // (208: those boarded, as the cabin seats them)
  }
  for (const f of state.dropships?.flights ?? []) {
    if (f.team !== team) continue;
    const pose = flightNow(state, f, tick)?.flight!.pose;
    if (!pose) continue;
    out.push({ id: f.id, team: f.team, planetIndex: f.planetIndex, dir: pose.dir, state: pose.phase === 'down' ? 'landed' : 'flying', wave: !!f.sortie,
      riders: ridersAboard(state, f).length, ...(pose.phase === 'in' ? { to: f.land } : {}) });
  }
  return out;
}

/** The mark's words under its glyph: what it is, and what it is doing. */
export function dropshipWords(m: DropshipMark): string {
  const what = m.wave ? 'wave ship' : 'dropship';
  if (m.state === 'parked') return `${what} · parked${m.riders ? ` · ${m.riders} aboard` : ''}`;
  return m.state === 'landed' ? `${what} · down${m.riders ? ` · ${m.riders} aboard` : ''}` : m.to ? `${what} · inbound${m.riders ? ` · ${m.riders}` : ''}` : `${what} · leaving`;
}
